import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorBanner from "../components/ErrorBanner";
import FinanceSectionTabs, { FINANCE_TAB_REPORTS } from "../components/FinanceSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import FinanceSummaryStatCards from "../components/finance/FinanceSummaryStatCards";
import { useAuth } from "../contexts/AuthContext";
import { isValidYyyyMmDd, toYmdFromDateLike } from "../shared/utils/calendarYmd";
import { formatCurrencyUsd, summarizeIncomeExpenseRows } from "../shared/utils/financeDisplay";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

const BUCKET_WEEKLY = "weekly";
const BUCKET_MONTHLY = "monthly";

function toUtcDateFromYmd(ymd) {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function getIsoWeekInfo(ymd) {
  const utcDate = toUtcDateFromYmd(ymd);
  const weekday = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - weekday);
  const isoYear = utcDate.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7);
  return { isoYear, week };
}

function getBucketKeyAndLabel(ymd, bucketType) {
  if (bucketType === BUCKET_WEEKLY) {
    const { isoYear, week } = getIsoWeekInfo(ymd);
    const key = `${isoYear}-${String(week).padStart(2, "0")}`;
    const label = `Week ${week}, ${isoYear}`;
    return { key, label };
  }

  const [year, month] = ymd.split("-");
  const key = `${year}-${month}`;
  const dt = toUtcDateFromYmd(`${year}-${month}-01`);
  const label = dt.toLocaleString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
  return { key, label };
}

function IncomeExpenseBarChart({ rows, onBarClick }) {
  const [barTooltip, setBarTooltip] = useState(null);

  if (!Array.isArray(rows) || rows.length === 0) {
    return <p className="panel-hint">No chart data for this range.</p>;
  }

  const width = 980;
  const height = 320;
  const padding = { top: 16, right: 16, bottom: 52, left: 64 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.income, r.expense)));
  const yScale = (value) => padding.top + chartH - (value / maxVal) * chartH;
  const groupWidth = chartW / Math.max(1, rows.length);
  const barWidth = Math.max(4, Math.min(26, groupWidth * 0.35));
  const tickCount = 5;

  function updateBarTooltip(e, row, kind) {
    const amount = kind === "income" ? row.income : row.expense;
    const label = kind === "income" ? "Income" : "Expense";
    setBarTooltip({
      x: e.clientX,
      y: e.clientY,
      period: row.label,
      seriesLabel: label,
      amount,
      color: kind === "income" ? "#16a34a" : "#dc2626",
    });
  }

  function moveBarTooltip(e) {
    setBarTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null));
  }

  return (
    <div className="panel" style={{ marginBottom: 0 }}>
      {barTooltip ? (
        <div
          role="tooltip"
          style={{
            position: "fixed",
            left: Math.max(
              8,
              Math.min(
                barTooltip.x + 12,
                (typeof window !== "undefined" ? window.innerWidth : 1000) - 228
              )
            ),
            top: Math.max(
              8,
              Math.min(barTooltip.y + 12, (typeof window !== "undefined" ? window.innerHeight : 800) - 80)
            ),
            zIndex: 10050,
            pointerEvents: "none",
            padding: "8px 12px",
            borderRadius: 8,
            background: "var(--panel-bg, #fff)",
            color: "var(--text-primary, #0f172a)",
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
            border: "1px solid var(--border-subtle, #e2e8f0)",
            fontSize: 13,
            lineHeight: 1.35,
            maxWidth: 220,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{barTooltip.period}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: barTooltip.color, flexShrink: 0 }} />
            <span>
              {barTooltip.seriesLabel}: {formatCurrencyUsd(barTooltip.amount)}
            </span>
          </div>
        </div>
      ) : null}
      <div style={{ overflowX: "auto" }}>
        <svg width={width} height={height} role="img" aria-label="Income and expense bar chart">
          {Array.from({ length: tickCount + 1 }).map((_, idx) => {
            const value = (maxVal * idx) / tickCount;
            const y = yScale(value);
            return (
              <g key={`y-${idx}`}>
                <line x1={padding.left} y1={y} x2={padding.left + chartW} y2={y} stroke="#e2e8f0" />
                <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#64748b">
                  {value === 0 ? "0" : `$${Math.round(value).toLocaleString()}`}
                </text>
              </g>
            );
          })}

          {rows.map((row, idx) => {
            const xCenter = padding.left + (idx + 0.5) * groupWidth;
            const incomeX = xCenter - barWidth - 2;
            const expenseX = xCenter + 2;
            const incomeY = yScale(row.income);
            const expenseY = yScale(row.expense);
            const incomeH = padding.top + chartH - incomeY;
            const expenseH = padding.top + chartH - expenseY;
            return (
              <g key={row.key}>
                <rect
                  x={incomeX}
                  y={incomeY}
                  width={barWidth}
                  height={incomeH}
                  fill="#16a34a"
                  rx="2"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => updateBarTooltip(e, row, "income")}
                  onMouseMove={moveBarTooltip}
                  onMouseLeave={() => setBarTooltip(null)}
                  onClick={() => onBarClick?.(row, "income")}
                />
                <rect
                  x={expenseX}
                  y={expenseY}
                  width={barWidth}
                  height={expenseH}
                  fill="#dc2626"
                  rx="2"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => updateBarTooltip(e, row, "expense")}
                  onMouseMove={moveBarTooltip}
                  onMouseLeave={() => setBarTooltip(null)}
                  onClick={() => onBarClick?.(row, "expense")}
                />
                <text x={xCenter} y={padding.top + chartH + 16} textAnchor="middle" fontSize="10" fill="#64748b">
                  {row.label}
                </text>
              </g>
            );
          })}

          <text x={padding.left + chartW / 2} y={height - 8} textAnchor="middle" fontSize="11" fill="#64748b">
            Time Interval
          </text>
          <text
            x={16}
            y={padding.top + chartH / 2}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            transform={`rotate(-90, 16, ${padding.top + chartH / 2})`}
          >
            Amount (USD)
          </text>
        </svg>
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, background: "#16a34a", borderRadius: 2 }} />
          Income
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, background: "#dc2626", borderRadius: 2 }} />
          Expense
        </span>
      </div>
    </div>
  );
}

function FinancialReports() {
  const { userInfo, loading: authLoading, authError } = useAuth();
  const navigate = useNavigate();
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [transactions, setTransactions] = useState([]);
  const [bucketType, setBucketType] = useState(BUCKET_MONTHLY);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading || !account_id) return;
    apiFetch(serverUrl + "/listTransactions", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setTransactions(list);
        const dateList = list
          .map((t) => toYmdFromDateLike(t?.date))
          .filter(Boolean)
          .sort();
        if (dateList.length > 0) {
          setStartDate((prev) => prev || dateList[0]);
          setEndDate((prev) => prev || dateList[dateList.length - 1]);
        }
      })
      .catch((err) => setError(getMessageForFetchError(err, "Unable to load financial reports.")));
  }, [account_id, authLoading, serverUrl]);

  const rangeError = useMemo(() => {
    if (startDate && !isValidYyyyMmDd(startDate)) return "Start date must use YYYY-MM-DD format.";
    if (endDate && !isValidYyyyMmDd(endDate)) return "End date must use YYYY-MM-DD format.";
    if (startDate && endDate && startDate > endDate) return "Start date cannot be after end date.";
    return "";
  }, [startDate, endDate]);

  const filtered = useMemo(() => {
    if (rangeError) return [];
    return (transactions || []).filter((t) => {
      const ymd = toYmdFromDateLike(t?.date);
      if (!ymd) return false;
      if (startDate && ymd < startDate) return false;
      if (endDate && ymd > endDate) return false;
      return true;
    });
  }, [transactions, startDate, endDate, rangeError]);

  const summary = useMemo(() => summarizeIncomeExpenseRows(filtered), [filtered]);

  const bucketedRows = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const ymd = toYmdFromDateLike(t?.date);
      if (!ymd) return;
      const amount = Number(t?.amount);
      if (!Number.isFinite(amount)) return;
      const bucket = getBucketKeyAndLabel(ymd, bucketType);
      if (!map[bucket.key]) {
        map[bucket.key] = {
          key: bucket.key,
          label: bucket.label,
          income: 0,
          expense: 0,
          startDate: ymd,
          endDate: ymd,
        };
      }
      if (ymd < map[bucket.key].startDate) map[bucket.key].startDate = ymd;
      if (ymd > map[bucket.key].endDate) map[bucket.key].endDate = ymd;
      if (amount >= 0) map[bucket.key].income += amount;
      else map[bucket.key].expense += Math.abs(amount);
    });

    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
  }, [filtered, bucketType]);

  const missingAccountMessage = !authLoading && !account_id && (authError || "Missing account information.");
  const displayError = error || missingAccountMessage || rangeError || "";

  function handleBarClick(row, amountType) {
    if (!row?.startDate || !row?.endDate) return;
    const params = new URLSearchParams({
      reportDrilldown: "1",
      startDate: row.startDate,
      endDate: row.endDate,
      amountType,
      bucketType,
      bucketLabel: row.label,
    });
    navigate(`/Transaction?${params.toString()}`);
  }

  if (authLoading) {
    return (
      <div style={{ minHeight: "calc(100vh - 70px)", padding: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="page-container page-container--section-tabs">
      <SectionTabsPageHeader subtitle="View income, expenses, and net cash flow with weekly or monthly trend buckets.">
        <FinanceSectionTabs activeTab={FINANCE_TAB_REPORTS} />
      </SectionTabsPageHeader>

      <ErrorBanner message={displayError} onDismiss={() => setError("")} />

      <div className="toolbar">
        <span className="toolbar-label">Start Date:</span>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="input"
          aria-label="Start date"
        />
        <span className="toolbar-label">End Date:</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="input"
          aria-label="End date"
        />
        <span className="toolbar-divider" />
        <span className="toolbar-label">View:</span>
        <div className="view-toggle">
          <button
            type="button"
            className={`btn ${bucketType === BUCKET_WEEKLY ? "active" : ""}`}
            onClick={() => setBucketType(BUCKET_WEEKLY)}
          >
            Weekly
          </button>
          <button
            type="button"
            className={`btn ${bucketType === BUCKET_MONTHLY ? "active" : ""}`}
            onClick={() => setBucketType(BUCKET_MONTHLY)}
          >
            Monthly
          </button>
        </div>
      </div>

      <FinanceSummaryStatCards
        totalIncome={summary.totalIncome}
        totalExpense={summary.totalExpense}
        netCashFlow={summary.netCashFlow}
      />

      <IncomeExpenseBarChart rows={bucketedRows} onBarClick={handleBarClick} />
    </div>
  );
}

export default FinancialReports;
